import React, { useState } from "react";
import { AlertTriangle, Lock, X } from "lucide-react";
import useLicenseStatus from "../hooks/useLicenseStatus";
import { formatGregorianDate } from "../utils/dateFormat";
import { toPersianDigits } from "../utils/numberFormat";

const DISMISS_KEY = "license-banner-dismissed";

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
};

const day = (iso) => (iso ? formatGregorianDate(iso.slice(0, 10)) : "—");

function bannerText(license) {
  if (license.status === "expiring") {
    const when = license.days_left > 0 ? `${toPersianDigits(license.days_left)} روز دیگر` : "امروز";
    return {
      title: "اشتراک رو به پایان است",
      body: `اشتراک این سامانه ${when} (${day(license.expires_at)}) به پایان می‌رسد. برای جلوگیری از توقف سرویس، آن را تمدید کنید.`,
    };
  }
  if (license.status === "grace") {
    return {
      title: "اشتراک به پایان رسیده است",
      body: `اشتراک در ${day(license.expires_at)} به پایان رسید. تا ${day(license.grace_ends_at)} همه امکانات فعال است و پس از آن سامانه فقط‌خواندنی می‌شود.`,
    };
  }
  return { title: "سامانه فقط‌خواندنی است", body: license.message };
}

/** Subscription warnings: renew soon, grace period, or read-only. */
export default function LicenseBanner() {
  const license = useLicenseStatus();
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!license?.enforced || license.status === "active") return null;
  const readOnly = license.read_only;
  // Warnings can be hidden for this session; read-only cannot.
  if (!readOnly && dismissed === `${license.status}:${license.expires_at}`) return null;

  const { title, body } = bannerText(license);
  const dismiss = () => {
    const value = `${license.status}:${license.expires_at}`;
    try {
      sessionStorage.setItem(DISMISS_KEY, value);
    } catch {
      // Private mode: hide it until the page reloads.
    }
    setDismissed(value);
  };

  const Icon = readOnly ? Lock : AlertTriangle;
  const tone =
    readOnly || license.status === "grace"
      ? "border-rose-200 bg-rose-50 text-rose-900"
      : "border-amber-200 bg-amber-50 text-amber-900";

  return (
    <div
      role={readOnly ? "alert" : "status"}
      dir="rtl"
      className={`fixed bottom-4 left-4 z-50 flex w-[calc(100%-2rem)] max-w-sm gap-3 rounded-xl border p-4 text-sm shadow-lg font-vazir ${tone}`}
    >
      <Icon aria-hidden="true" size={20} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-bold">{title}</p>
        <p className="mt-1 leading-6">{body}</p>
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={dismiss}
          aria-label="بستن"
          className="-m-1 self-start rounded p-1 opacity-70 hover:opacity-100"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
